/**
 * Helpers for Platform.Schema.Flow.Settings.GlobalVar typed Value* fields
 * and save/delete planning for the Shared variables grid.
 */

export const GLOBAL_VAR_API = 'Apps/Platform/Schema/Flow/Settings/GlobalVar';

/**
 * Stored Collection for the Global bucket.
 * Not a user-typed name, so it does not appear among custom collections.
 */
export const GLOBAL_COLLECTION_ID = 'Platform.Global';

/** Project variables are stored with Collection set to the project id. */
export function projectCollectionId(projectId) {
    return projectId == null ? '' : String(projectId).trim();
}

/**
 * Blank (legacy global), Platform.Global, and the current project id
 * are built-in buckets, not custom collection names.
 */
export function isBuiltinCollection(name, projectId) {
    const n = name == null ? '' : String(name).trim();
    if (!n)
        return true;
    if (n === GLOBAL_COLLECTION_ID)
        return true;
    const project = projectCollectionId(projectId);
    return !!project && n === project;
}

/**
 * Prefer Platform.Global when the same name exists in the legacy blank collection.
 * Rows with any other Collection are ignored.
 * Returned rows use Collection = GLOBAL_COLLECTION_ID for saving.
 */
export function mergeGlobalRows(primaryRows, legacyRows) {
    const byName = new Map();
    for (const row of legacyRows || []) {
        if (!isBlankCollection(row?.Collection))
            continue;
        const name = row?.Name == null ? '' : String(row.Name);
        if (!name)
            continue;
        byName.set(name, { ...row, Name: name, Collection: '' });
    }
    for (const row of primaryRows || []) {
        if (row?.Collection != null && String(row.Collection).trim() !== GLOBAL_COLLECTION_ID)
            continue;
        const name = row?.Name == null ? '' : String(row.Name);
        if (!name)
            continue;
        byName.set(name, { ...row, Name: name, Collection: GLOBAL_COLLECTION_ID });
    }
    return [...byName.values()].map((row) => ({
        ...row,
        Collection: GLOBAL_COLLECTION_ID,
    }));
}

function isBlankCollection(value) {
    return value == null || String(value).trim() === '';
}

/** Global bucket only: Platform.Global, plus legacy rows stored with a blank Collection. */
export function selectGlobalRows(rows) {
    const primary = [];
    const legacy = [];
    for (const row of rows || []) {
        if (!row || row.Collection == null)
            continue;
        const collection = String(row.Collection).trim();
        if (collection === GLOBAL_COLLECTION_ID)
            primary.push(row);
        else if (collection === '')
            legacy.push(row);
    }
    return { primary, legacy, rows: mergeGlobalRows(primary, legacy) };
}

/** Drop rows that belong to a different collection when the payload includes Collection. */
export function rowsMatchingCollection(rows, collection) {
    const want = collection == null ? '' : String(collection);
    return (rows || []).filter((row) => {
        if (!row)
            return false;
        if (row.Collection == null)
            return true;
        return String(row.Collection) === want;
    });
}

export const TYPE_OPTIONS = [
    { value: 'string', name: 'Text' },
    { value: 'int', name: 'Whole number' },
    { value: 'float', name: 'Fractional number' },
    { value: 'bool', name: 'True or false' },
    { value: 'object', name: 'Data (advanced)' },
];

export const TYPE_VALUES = TYPE_OPTIONS.map((o) => o.value);

/** FlowV2 identifier: letter or underscore, then letters, digits, or underscores. */
const VARIABLE_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/;

export function isValidVariableName(name) {
    return VARIABLE_NAME.test(name == null ? '' : String(name).trim());
}

export function variableNameError(name) {
    const n = name == null ? '' : String(name).trim();
    if (!n)
        return 'Every variable needs a name.';
    if (!VARIABLE_NAME.test(n))
        return `“${n}” is not a valid variable name. Start with a letter or underscore, then use only letters, digits, and underscores.`;
    return null;
}

const VALUE_FIELDS = ['ValueString', 'ValueInteger', 'ValueFloat', 'ValueBoolean', 'ValueObject'];

const TYPE_TO_FIELD = {
    string: 'ValueString',
    int: 'ValueInteger',
    float: 'ValueFloat',
    bool: 'ValueBoolean',
    object: 'ValueObject',
};

export function valueFieldForType(type) {
    return TYPE_TO_FIELD[type] || TYPE_TO_FIELD.string;
}

export function typeLabel(type) {
    const opt = TYPE_OPTIONS.find((o) => o.value === type);
    return opt ? opt.name : (type || 'Text');
}

export function recordKey(collection, name) {
    return `${collection == null ? '' : String(collection)}\0${name == null ? '' : String(name)}`;
}

export function parseRecordKey(key) {
    const i = key.indexOf('\0');
    if (i < 0)
        return { Collection: '', Name: key };
    return { Collection: key.slice(0, i), Name: key.slice(i + 1) };
}

/** Clear Value* fields that do not match the current Type. */
export function clearNonMatchingValueFields(row) {
    if (!row || typeof row !== 'object')
        return row;
    const keep = valueFieldForType(row.Type || 'string');
    for (const f of VALUE_FIELDS) {
        if (f !== keep && f in row)
            delete row[f];
    }
    return row;
}

/**
 * Display value for the Value column. object → JSON string; others → raw typed field.
 */
export function getDisplayValue(row) {
    if (!row)
        return '';
    const type = row.Type || 'string';
    const field = valueFieldForType(type);
    const raw = row[field];
    if (type === 'object') {
        if (raw === undefined || raw === null)
            return '';
        if (typeof raw === 'string')
            return raw;
        try {
            return JSON.stringify(raw, null, 2);
        }
        catch {
            return String(raw);
        }
    }
    if (type === 'bool')
        return !!raw;
    if (raw === undefined || raw === null)
        return type === 'string' ? '' : null;
    return raw;
}

/**
 * Write a display/editor value into the correct Value* field and clear others.
 * For object, accepts a JSON string (parsed) or already-parsed value.
 * Returns { ok, error? }.
 */
export function setDisplayValue(row, displayValue) {
    if (!row || typeof row !== 'object')
        return { ok: false, error: 'Missing row' };

    const type = row.Type || 'string';
    const field = valueFieldForType(type);

    if (type === 'object') {
        if (displayValue === '' || displayValue == null) {
            row.ValueObject = null;
        }
        else if (typeof displayValue === 'string') {
            try {
                row.ValueObject = JSON.parse(displayValue);
            }
            catch (e) {
                return { ok: false, error: e?.message || 'Invalid JSON' };
            }
        }
        else {
            row.ValueObject = displayValue;
        }
    }
    else if (type === 'bool') {
        row.ValueBoolean = !!displayValue;
    }
    else if (type === 'int') {
        if (displayValue === '' || displayValue == null)
            row.ValueInteger = null;
        else {
            const n = Number(displayValue);
            if (!Number.isFinite(n) || !Number.isInteger(n))
                return { ok: false, error: 'Whole number required' };
            row.ValueInteger = n;
        }
    }
    else if (type === 'float') {
        if (displayValue === '' || displayValue == null)
            row.ValueFloat = null;
        else {
            const n = Number(displayValue);
            if (!Number.isFinite(n))
                return { ok: false, error: 'Number required' };
            row.ValueFloat = n;
        }
    }
    else {
        row.ValueString = displayValue == null ? '' : String(displayValue);
    }

    clearNonMatchingValueFields(row);
    return { ok: true };
}

/** Apply a Type change: set Type, seed a sensible default Value*, clear others. */
export function applyTypeChange(row, newType) {
    const type = TYPE_TO_FIELD[newType] ? newType : 'string';
    row.Type = type;
    const field = valueFieldForType(type);
    if (row[field] === undefined) {
        if (type === 'bool')
            row.ValueBoolean = false;
        else if (type === 'string')
            row.ValueString = '';
        else if (type === 'int')
            row.ValueInteger = 0;
        else if (type === 'float')
            row.ValueFloat = 0;
        else if (type === 'object')
            row.ValueObject = {};
    }
    clearNonMatchingValueFields(row);
    return row;
}

/**
 * Build API save body from a grid row (only Collection, Name, Type, matching Value*).
 */
export function buildSavePayload(row, collection) {
    const type = row?.Type || 'string';
    const field = valueFieldForType(type);
    const payload = {
        Collection: collection == null ? '' : String(collection),
        Name: row?.Name == null ? '' : String(row.Name),
        Type: type,
    };
    if (row && field in row)
        payload[field] = row[field];
    else if (type === 'bool')
        payload.ValueBoolean = false;
    else if (type === 'string')
        payload.ValueString = '';
    return payload;
}

/**
 * Stable comparison payload for dirty detection (ignores UI-only fields).
 */
export function normalizeForCompare(row, collection) {
    return JSON.stringify(buildSavePayload(row, collection));
}

/**
 * Plan deletes and posts from an original snapshot map and current rows.
 * @param {Map<string, object>|Record<string, object>} originalByKey - key = recordKey(collection, name)
 * @param {object[]} currentRows
 * @param {string} collection
 * @returns {{ toDelete: {Collection:string,Name:string}[], toSave: object[] }}
 */
export function planSaveChanges(originalByKey, currentRows, collection) {
    const orig = originalByKey instanceof Map
        ? originalByKey
        : new Map(Object.entries(originalByKey || {}));

    const currentKeys = new Set();
    const toSave = [];

    for (const row of currentRows || []) {
        const name = row?.Name == null ? '' : String(row.Name).trim();
        if (!name)
            continue;
        const key = recordKey(collection, name);
        currentKeys.add(key);
        const payload = buildSavePayload({ ...row, Name: name }, collection);
        const prev = orig.get(key);
        const isNew = !!row._isNew || !prev;
        const changed = isNew || normalizeForCompare(prev, collection) !== normalizeForCompare(payload, collection);
        if (changed)
            toSave.push(payload);
    }

    const toDelete = [];
    for (const [key] of orig) {
        if (!currentKeys.has(key))
            toDelete.push(parseRecordKey(key));
    }

    return { toDelete, toSave };
}

export function createEmptyRow(collection) {
    return {
        Collection: collection == null ? '' : String(collection),
        Name: 'unnamed',
        Type: 'string',
        ValueString: '',
        _isNew: true,
        _rowId: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    };
}

export function rowIdFor(row, collection) {
    if (row?._rowId)
        return row._rowId;
    return recordKey(collection, row?.Name);
}
